import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Formats FastAPI error details (422s arrive as arrays, not strings).
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [shop, setShop] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/signup`, {
        email: email.trim(),
        password,
        shop: shop.trim(),
      });
      if (!data?.ok) throw new Error("signup failed");
      // Same session as the magic-link flow — but self-serve signups land back on
      // the landing page: the studio opens once a Razorpay plan activates credits.
      localStorage.setItem("grapho_customer_email", data.customer.email);
      navigate("/", { state: { signupSuccess: true } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#070B14] px-6">
      <form
        onSubmit={submit}
        data-testid="signup-form"
        className="glass w-full max-w-md rounded-3xl p-8"
      >
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-400/90">
          Grapho Studio
        </p>
        <p className="mt-2 text-sm text-slate-400">
          Join Etsy sellers who wake up to finished designs every morning.
        </p>

        <h1 className="mt-5 text-2xl font-bold tracking-tight text-white">Create your account</h1>
        <p className="mt-2 text-sm text-slate-400">Free to start. Design credits come with your plan.</p>

        <label className="mt-6 block text-xs font-medium uppercase tracking-wider text-slate-400">
          Email
          <input
            data-testid="signup-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourshop.com"
            className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
          />
        </label>

        <label className="mt-4 block text-xs font-medium uppercase tracking-wider text-slate-400">
          Password
          <input
            data-testid="signup-password-input"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
          />
        </label>

        <label className="mt-4 block text-xs font-medium uppercase tracking-wider text-slate-400">
          Etsy shop name or URL{" "}
          <span className="normal-case text-slate-600">(recommended)</span>
          <input
            data-testid="signup-shop-input"
            type="text"
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            placeholder="yourshop.etsy.com"
            className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none"
          />
        </label>

        {error && (
          <p data-testid="signup-error" className="mt-4 text-sm text-rose-400">
            {error}
          </p>
        )}

        <button
          data-testid="signup-submit-button"
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-xl bg-amber-400 px-5 py-3 text-sm font-bold text-[#070B14] transition hover:bg-amber-300 disabled:opacity-60"
        >
          {busy ? "Creating your studio..." : "Create my studio"}
        </button>

        <div className="mt-5 text-sm">
          <Link
            to="/login"
            data-testid="signup-login-link"
            className="text-amber-300 transition-colors hover:text-amber-200"
          >
            Already have an account? Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
